/* istanbul ignore file */
import { Cluster, Renderer } from '@googlemaps/markerclusterer';
import { Status } from '@googlemaps/react-wrapper';
import { styled } from '@mui/material/styles';

import { palette, px, rem, Theme } from '../../theme';
import { ButtonBase } from '../Button';
import { ButtonLink } from '../ButtonLink';
import { SearchInput } from '../Forms/SearchInput';
import { Heading } from '../Heading';
import { ImageStandaloneProperties } from '../ImageStandalone/ImageStandalone.types';

interface StyledMapContainerProperties {
    variant: 'short' | 'tall';
}

interface StyledMapPlaceholderProperties {
    visible: boolean;
}

export const StyledMapContainer = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        width: 100%;
        height: 100%;

        .info-window-container {
            display: grid;
            grid-auto-flow: row;
            grid-row-gap: ${theme.tggSpacing(rem(1.2), 5)};
            max-width: ${theme.tggSpacing(rem(24), 96)};
        }
	`}

    .gym-name {
        ${({ theme }: { theme: Theme }) => theme.typography.text_23_bold};
        ${({ theme }: { theme: Theme }) => `
            color: ${theme.palette.primary.main};
            text-decoration: underline;
            text-transform: lowercase;
            cursor: pointer;
	    `}
    }

    .gym-address {
        ${({ theme }: { theme: Theme }) => theme.typography.text_15};
        ${({ theme }: { theme: Theme }) => `
            color: ${theme.palette.common.blue};
            text-decoration: underline;
            cursor: pointer;
	    `}
    }

    .gym-lowest-price {
        ${({ theme }: { theme: Theme }) => theme.typography.text_15};
    }

    .gym-cta-container {
        display: grid;
        grid-auto-flow: column;

        button {
            ${({ theme }: { theme: Theme }) => theme.typography.text_16};
            ${({ theme }: { theme: Theme }) => `
                width: 100%;
                padding: ${theme.tggSpacing(rem(0.5), 2)};
                color: ${theme.palette.common.blue};
                text-transform: lowercase;
                cursor: pointer;
                background-color: ${theme.palette.common.white};
                border: ${theme.tggSpacing(rem(0.1), 0.5)}
                    solid ${theme.palette.common.blue};
                border-radius: ${theme.tggSpacing(rem(10), 40)};
	        `}
        }

        button:hover {
            ${({ theme }: { theme: Theme }) => `
                color: ${theme.palette.common.white};
                background-color: ${theme.palette.common.blue};
                border: ${theme.tggSpacing(px(1), 0.25)}
                    solid transparent;
	        `}
        }
    }
`;

export const PlaceholderMapContainer = styled(
    'div',
)<StyledMapContainerProperties>`
    ${({ theme, variant, visible }) => `
        position: relative;
        height: ${theme.tggSpacing(rem(40), 100)};

        margin-bottom: ${theme.tggSpacing(rem(2), 8)};

        ${theme.breakpoints.up('desktop')} {
            max-width: ${theme.tggSpacing(rem(128), 512)};
            height: ${theme.tggSpacing(rem(variant === 'short' ? 40 : 80), variant === 'short' ? 160 : 320)};
            margin: ${theme.tggSpacing([0, 'auto', variant === 'short' ? 3 : 6, 'auto'], [0, 'auto', variant === 'short' ? 12 : 24, 'auto'])};
        }
        ${
            visible
                ? ''
                : `
                    position: absolute;
                    width: ${theme.tggSpacing(rem(0.1), 0.5)};
                    height: ${theme.tggSpacing(rem(0.1), 0.5)};
                    overflow: hidden;
                    white-space: nowrap;
                    clip: rect(0 0 0 0);
                    clip-path: inset(50%);
                `
        };
    `}
`;

export const StyledMapPlaceholder = styled(
    'div',
)<StyledMapPlaceholderProperties>`
    ${({ theme, visible }) => `
        position: absolute;
        top: ${theme.tggSpacing(0, 0)};
        right: ${theme.tggSpacing(0, 0)};
        bottom: ${theme.tggSpacing(0, 0)};
        left: ${theme.tggSpacing(0, 0)};
        z-index: 1;
        display: ${visible ? 'block' : 'none'};

        width: 100%;
        height: 100%;
	`}
`;

export const StyledListerActionsContainer = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        display: flex;
        flex-direction: column;
        gap: ${theme.tggSpacing(rem(2), 8)};
        justify-content: center;
        max-width: ${theme.tggSpacing(rem(112.4), 449.5)};
        padding-inline: ${theme.tggSpacing(rem(3), 12)};
        margin-inline: ${theme.tggSpacing('auto', 'auto')};

        ${theme.breakpoints.up('desktop')} {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
        }
	`}
`;

export const StyledBackToSearch = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        display: flex;
        align-items: center;
        justify-content: center;
        text-decoration: underline;

        svg g {
            stroke: ${theme.palette.primary.main};
        }

        ${theme.breakpoints.up('desktop')} {
            max-width: ${theme.tggSpacing(rem(128), 512)}; /* Match width of map container */
        }
	`}
`;

export const StyledSearchDropdown = styled('div')`
    ${({ theme }: { theme: Theme }) => theme.typography.text_14};
    ${({ theme }: { theme: Theme }) => `
        position: relative;
        width: 100%;
        max-width: ${theme.tggSpacing(rem(36), 144)};
        margin-inline: ${theme.tggSpacing('auto', 'auto')};
        ${theme.breakpoints.up('desktop')} {
            max-width: ${theme.tggSpacing(rem(26.5), 106)};
            margin-inline: ${theme.tggSpacing(0, 0)};
        }


        label {
            position: absolute;
            left: ${theme.tggSpacing(0, 0)};
            width: 100%;
            line-height: 4.4rem;
            text-align: center;

            ${theme.breakpoints.up('desktop')} {
                left: ${theme.tggSpacing(rem(2.5), 10)};
                text-align: left;
            }
        }

        strong {
            font-family: thegymgroupsans-headline;
        }

        select {
            appearance: none;
            opacity: 0;
        }

        svg {
            top: calc(50% - ${theme.tggSpacing(rem(1), 4)});
            width: ${theme.tggSpacing(rem(1.6), 6.5)};
        }
	`}
`;

export const StyledRegionalPreference = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        max-width: ${theme.tggSpacing(rem(52), 208)};
        margin: ${theme.tggSpacing([rem(4), rem(2), 0], [16, 8, 0])};

        ${theme.breakpoints.up('desktop')} {
            margin: ${theme.tggSpacing([rem(4), 'auto', 0], [16, 'auto', 0])};
            text-align: center;
        }
	`}
`;

export const StyledRegionalPreferenceHeading = styled(Heading)`
    ${({ theme }) => theme.typography.text_25_bold_desktop_27}
`;

export const StyledButtonLink = styled(ButtonLink)`
    ${({ theme }: { theme: Theme }) => theme.typography.bodySmall};
    ${({ theme }: { theme: Theme }) => `
         margin-left: ${theme.tggSpacing(rem(1.5), 6)};
	`}
`;

export const GymMarkerIcon =
    'data:image/svg+xml;base64,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';

/**
 * React components cannot be used directly as the marker image.
 * This is the rendered HTML from the `targetLocation` `<Icon />` component, converted into base64.
 */
export const resultMarkerIcon =
    'data:image/svg+xml;base64,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';

/**
 * How to display any status text returned from the map
 * @param status Map status - Loading / Success / Failure
 */
export const render = (status: Status) => <h2>{status}</h2>;

/**
 * Custom renderer for marker clusters. Required to change the icon used
 */
export class ClusterRenderer implements Renderer {
    public render(cluster: Cluster) {
        /**
         * React components cannot be used directly as the icon, so we create
         * an SVG inline and convert it to a base64 URL.
         */
        const svg = btoa(`
            <svg fill="${palette.primary.main}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
                <circle cx="120" cy="120" opacity="1" r="70" />
                <g fill="none" stroke="none" stroke-width="12">
                    <circle cx="120" cy="120" opacity="1" r="76" />
                </g>
            </svg>`);
        const { count, position } = cluster;

        return new google.maps.Marker({
            position,
            icon: {
                url: `data:image/svg+xml;base64,${svg}`,
                scaledSize: new google.maps.Size(45, 45),
            },
            label: {
                text: String(count),
                color: palette.secondary.main,
                fontSize: '12px',
                fontWeight: 'bold',
            },
            title: `Cluster of ${count} markers`,
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
        });
    }
}

export const StyledSearchInput = styled(SearchInput)`
    ${({ theme }: { theme: Theme }) => `
        width: 100%;
        margin: ${theme.tggSpacing([0, 0, rem(2), 0], [0, 0, 8, 0])};
        ${theme.breakpoints.up('desktop')} {
            max-width: ${theme.tggSpacing(rem(47), 188)};
            margin: ${theme.tggSpacing([0, rem(2), 0, 0], [0, 8, 0, 0])};
        }
	`}
`;

export const StyledFindMyLocationButton = styled(ButtonBase)`
    ${({ theme }: { theme: Theme }) => theme.typography.text_18_bold};
    ${({ theme }: { theme: Theme }) => `
        min-width: ${theme.tggSpacing(rem(26.8), 107)};
	`}
`;

export const StyledSearchContainer = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        padding: ${theme.tggSpacing([px(20), px(30)], [5, 7.5])};
        ${theme.breakpoints.up('desktop')} {
            display: flex;
            justify-content: center;
            padding: ${theme.tggSpacing([px(30), px(30)], [7.5, 7.5])};
            margin: ${theme.tggSpacing([0, 'auto'], [0, 'auto'])};
        }
	`}
`;

export const StyledAlertContainer = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        padding: ${theme.tggSpacing([0, px(30), px(20), px(30)], [0, 7.5, 5, 7.5])};

        ${theme.breakpoints.up('desktop')} {
            display: flex;
            justify-content: center;
            padding: ${theme.tggSpacing([0, px(30), px(30), px(30)], [0, 7.5, 7.5, 7.5])};
        }
	`}
`;

export const TilesWrapper = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        margin: ${theme.tggSpacing([0, 'auto'], [0, 'auto'])};
	`}
`;

export const StyledFilterLabelWrapper = styled('span')`
    ${({ theme }: { theme: Theme }) => `
        display: flex;
        gap: ${theme.tggSpacing(rem(0.6), 2.5)};

        ${theme.breakpoints.down(768)} {
            justify-content: center;
        }
	`}
`;

export const getOverrideSizes = (
    height: number,
    maxMobileHeight: number,
): ImageStandaloneProperties['overrideSizes'] => {
    return {
        default: { height, width: 'auto' },
        smallMobile: {
            height: maxMobileHeight,
            width: 'auto',
        },
        mobile: {
            height: maxMobileHeight,
            width: 'auto',
        },
        desktop: { height, width: 'auto' },
        largeDesktop: {
            height,
            width: 'auto',
        },
    };
};
